import dashboardApi from 'services/api/dashboard'

import { brandVariants } from '../main/hotelBrandsAndChains'
import * as t from './actionTypes'

// helpers

function findMatchingBrand(subsidiaryName) {
  return brandVariants.find(b => subsidiaryName.match(new RegExp(b, 'i')))
}

function getRichHotelData(hotelData) {
  const richHotelData = hotelData.map(h => {
    const brand = findMatchingBrand(h.hotelName)

    // Normalize property names - handle both PascalCase and camelCase from API
    const normalized = {
      ...h,
      // Ensure camelCase properties exist (fallback to PascalCase if needed)
      serviceStartDate: h.serviceStartDate || h.ServiceStartDate,
      addedOn: h.addedOn || h.AddedOn,
      serviceBlockOn: h.serviceBlockOn || h.ServiceBlockOn,
      pepDesktopAppDeviceCount: h.pepDesktopAppDeviceCount ?? h.PepDesktopAppDeviceCount ?? 0,
      pepDesktopAppDistinctUserCount:
        h.pepDesktopAppDistinctUserCount ?? h.PepDesktopAppDistinctUserCount ?? null,
      pepDashboardUserCount: h.pepDashboardUserCount ?? h.PepDashboardUserCount ?? null,
      pepDesktopAppVersions: h.pepDesktopAppVersions ?? h.PepDesktopAppVersions ?? '',
      searchKey: `#${h.displayId} ${h.hotelName} ${h.chainCode} ${h.groupName} ${h.parentChainCode}`,
      key: h.subsidiaryId,
      brand,
      shortName: brand
        ? h.hotelName.replace(new RegExp(brand, 'i'), '').trim()
        : h.hotelName
    }

    return normalized
  })

  return richHotelData
}

function generateHotelGroups(hotelData) {
  return hotelData.reduce((prev, curr) => {
    if (!curr.groupName) {
      return prev
    }

    return { ...prev, [curr.groupName]: curr.groupName }
  }, {})
}

function generateHotelChains(hotelData) {
  const hotelChains = hotelData.reduce((prev, curr) => {
    if (!curr.chainCode) {
      return prev
    }

    return { ...prev, [curr.chainCode]: curr.chainCode }
  }, {})

  return hotelChains
}

// actions

export const getHealthCheckInvoices = () => {
  return async dispatch => {
    dispatch({
      type: t.GET_HEALTH_CHECK_INVOICES_STARTED
    })

    try {
      const hotelData = await dashboardApi.getHealthCheckInvoices()

      dispatch({
        type: t.GET_HEALTH_CHECK_INVOICES_SUCCESS,
        payload: {
          months: hotelData.months,
          hotelData: getRichHotelData(hotelData.subsidiaries),
          hotelGroups: generateHotelGroups(hotelData.subsidiaries),
          hotelChains: generateHotelChains(hotelData.subsidiaries)
        }
      })
    } catch (err) {
      dispatch({
        type: t.GET_HEALTH_CHECK_INVOICES_FAILED
      })
    }
  }
}

export const getHealthCheckBillingData = () => {
  return async dispatch => {
    dispatch({
      type: t.GET_HEALTH_CHECK_SYNC_BILLING_STARTED
    })

    try {
      const hotelData = await dashboardApi.getHealthCheckBilling()

      // Debug: Log first hotel to check API response structure
      if (hotelData && hotelData.length > 0) {
        const firstHotel = hotelData[0]
        console.log('🔍 Billing API Response - First Hotel:', {
          subsidiaryId: firstHotel.subsidiaryId,
          displayId: firstHotel.displayId,
          hotelName: firstHotel.hotelName,
          // Check both PascalCase and camelCase
          serviceStartDate: firstHotel.serviceStartDate,
          ServiceStartDate: firstHotel.ServiceStartDate,
          addedOn: firstHotel.addedOn,
          AddedOn: firstHotel.AddedOn,
          serviceBlockOn: firstHotel.serviceBlockOn,
          ServiceBlockOn: firstHotel.ServiceBlockOn,
          // Show all property names
          allPropertyNames: Object.keys(firstHotel).filter(k => 
            k.toLowerCase().includes('date') || 
            k.toLowerCase().includes('added') || 
            k.toLowerCase().includes('start')
          )
        })
        console.log('🔍 All properties:', Object.keys(firstHotel))
      }

      dispatch({
        type: t.GET_HEALTH_CHECK_SYNC_BILLING_SUCCESS,
        payload: {
          hotelData: getRichHotelData(hotelData),
          hotelGroups: generateHotelGroups(hotelData),
          hotelChains: generateHotelChains(hotelData)
        }
      })
    } catch (err) {
      dispatch({
        type: t.GET_HEALTH_CHECK_SYNC_BILLING_FAILED
      })
    }
  }
}

export const getHealthCheckSyncData = () => {
  return async dispatch => {
    dispatch({
      type: t.GET_HEALTH_CHECK_SYNC_DATA_STARTED
    })

    try {
      const hotelData = await dashboardApi.getHealthCheckSync()

      dispatch({
        type: t.GET_HEALTH_CHECK_SYNC_DATA_SUCCESS,
        payload: {
          hotelData: getRichHotelData(hotelData),
          hotelGroups: generateHotelGroups(hotelData),
          hotelChains: generateHotelChains(hotelData)
        }
      })
    } catch (err) {
      dispatch({
        type: t.GET_HEALTH_CHECK_SYNC_DATA_FAILED
      })
    }
  }
}

export const getHealthCheckAutoInviteData = (periodStart, periodEnd) => {
  return async dispatch => {
    dispatch({
      type: t.GET_HEALTH_CHECK_AUTOINVITE_DATA_STARTED
    })

    try {
      const hotelData = await dashboardApi.getHealthCheckInvite(periodStart, periodEnd)

      dispatch({
        type: t.GET_HEALTH_CHECK_AUTOINVITE_DATA_SUCCESS,
        payload: {
          hotelData: getRichHotelData(hotelData),
          hotelGroups: generateHotelGroups(hotelData),
          hotelChains: generateHotelChains(hotelData)
        }
      })
    } catch (err) {
      if (err) {
        dispatch({
          type: t.GET_HEALTH_CHECK_AUTOINVITE_DATA_FAILED
        })

        return
      }
    }
  }
}
